import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, Check, Loader2, Music2, Copy } from 'lucide-react';
import { Button } from './ui/button';

const DAYS = [
  { key: 'monday', label: 'Mon' }, { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' }, { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' }, { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

/**
 * Simple per-day playlist schedule for a commercial outlet: pick one assigned
 * playlist per day (it fills that whole day). The parent owns the API call.
 */
export const CommercialScheduleEditor = ({ selection, playlists = [], onSave, saving = false }) => {
  const [draft, setDraft] = useState(() => {
    const base = {};
    DAYS.forEach((d) => { base[d.key] = (selection && selection[d.key]) || null; });
    return base;
  });

  const baseline = useMemo(() => JSON.stringify(
    DAYS.reduce((a, d) => { a[d.key] = (selection && selection[d.key]) || null; return a; }, {}),
  ), [selection]);
  const dirty = JSON.stringify(draft) !== baseline;

  const nameOf = (id) => (playlists.find((p) => p.id === id)?.name) || '—';

  const setDay = (day, val) => setDraft((p) => ({ ...p, [day]: val || null }));
  const applyToAll = (day) => {
    const v = draft[day] || null;
    setDraft(DAYS.reduce((a, d) => { a[d.key] = v; return a; }, {}));
    toast.success('Applied to every day');
  };

  const save = async () => {
    try {
      const res = await onSave(draft);
      if (res && res.selection) {
        const base = {};
        DAYS.forEach((d) => { base[d.key] = res.selection[d.key] || null; });
        setDraft(base);
      }
      toast.success('Schedule saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save schedule');
    }
  };

  if (playlists.length === 0) {
    return (
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 text-sm text-gray-400" data-testid="commercial-schedule-empty">
        No playlists are assigned to this outlet yet. Ask your admin to assign playlists, then set a day-by-day schedule here.
      </div>
    );
  }

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5" data-testid="commercial-schedule-editor">
      <div className="flex items-center gap-2 mb-4">
        <CalendarDays className="w-4 h-4 text-[#00FFFF]" />
        <h4 className="text-sm font-bold text-white uppercase tracking-wider">Weekly Playlist Schedule</h4>
      </div>
      <div className="space-y-2">
        {DAYS.map((d) => (
          <div key={d.key} className="flex items-center gap-2" data-testid={`commercial-schedule-row-${d.key}`}>
            <span className="w-10 text-xs font-bold text-gray-400 uppercase">{d.label}</span>
            <div className="relative flex-1">
              <Music2 className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                value={draft[d.key] || ''}
                onChange={(e) => setDay(d.key, e.target.value)}
                style={{ colorScheme: 'dark' }}
                className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-2 py-2 text-xs text-white focus:outline-none focus:border-[#00FFFF]/50"
                data-testid={`commercial-schedule-select-${d.key}`}
              >
                <option value="">— Nothing scheduled —</option>
                {playlists.map((p) => (
                  <option key={p.id} value={p.id}>{`${p.name} (${p.track_count} tracks)`}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => applyToAll(d.key)}
              title="Apply this day to all"
              className="text-gray-500 hover:text-[#00FFFF] p-1.5"
              data-testid={`commercial-schedule-applyall-${d.key}`}
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="commercial-schedule-save"
          className={`h-9 px-6 rounded-full font-bold text-xs ${dirty ? 'bg-[#39FF14] text-black hover:bg-[#2fd60f]' : 'bg-white/10 text-gray-400'}`}
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : dirty ? 'Save schedule' : <><Check className="w-4 h-4 mr-2" /> Saved</>}
        </Button>
      </div>
    </div>
  );
};

export default CommercialScheduleEditor;
